import { useEffect } from "react";
import {
  Link,
  createFileRoute,
  getRouteApi,
  notFound,
  redirect,
} from "@tanstack/react-router";
import { trackPageView } from "@/hooks/useAnalytics";
import {
  getJobBySlug,
  getJobs,
  getSubscriberCount,
  getTagBySlug,
  getTagStats,
} from "@/lib/api";
import type { PaginatedJobs } from "@/types/db";
import JobCard from "@/components/common/jobs/jobCard";
import JobDetailContent from "@/components/common/jobs/jobDetailContent";
import JobPostingJsonLd from "@/components/common/jobs/jobPostingJsonLd";
import SearchClient from "@/components/search/searchClient";
import type { JobWithRelations } from "@/types/db";
import Hero from "@/components/hero";
import EmailCaptureForm from "@/components/common/emailCaptureForm";
import NotFoundPage from "@/components/common/NotFoundPage";
import TagListingView from "@/components/common/tagListingView";

import { findLocationBySlug } from "@/lib/locations";
import BreadcrumbJsonLd from "@/components/common/breadcrumbJsonLd";
import type { TagStatsData } from "@/lib/faqSchema";
import { jsonLdSafe } from "@/lib/jsonLd";
import {
  getTagMetaDescription,
  getTagMetaTitle,
  toFieldName,
} from "@/lib/tagCopy";
import { splitTags } from "@/lib/tagFilterUrl";
import { shouldNoIndex } from "@/lib/jobLifecycle";
import { getJobMeta, getJobBreadcrumbs } from "@/lib/jobSeo";
import { MIN_INDEXABLE_JOBS } from "@/lib/listingSeo";

const SITE_URL = "https://datafirstjobs.com";
const rootRoute = getRouteApi("__root__");

interface SlugSearch {
  /** Comma-separated slugs; parsed in loader via splitTags(). */
  tags?: string;
}

export const Route = createFileRoute("/$slug")({
  validateSearch: (search: Record<string, unknown>): SlugSearch => ({
    tags: typeof search.tags === "string" ? search.tags : undefined,
  }),
  loaderDeps: ({ search: { tags } }) => ({ tags }),
  loader: async ({ params, context, deps }) => {
    if (params.slug === "remote-data-jobs") {
      throw redirect({ to: "/remote", statusCode: 301 });
    }
    const locations = context.locations;
    const extraTags = splitTags(deps.tags);

    // Check if slug matches a known location (fast, no API call)
    const location = findLocationBySlug(params.slug, locations);
    if (location) {
      const initialData = (await getJobs({
        data: {
          limit: 20,
          country: location.country,
          state: location.state,
          ...(extraTags.length > 0 ? { tags: extraTags } : {}),
        },
      })) as PaginatedJobs;
      return {
        type: "location" as const,
        displayName: location.displayName,
        initialJobs: initialData.jobs,
        initialTotal: initialData.total,
        country: location.country,
        state: location.state,
        slug: params.slug,
        extraTagSlugs: extraTags,
      };
    }

    // Try tag and job lookup in parallel (both 404 → unknown slug, render
    // NotFoundPage). `.catch(() => null)` keeps a transient API failure from
    // crashing SSR — we treat it as "couldn't resolve" instead of throwing.
    const [tag, job] = await Promise.all([
      getTagBySlug({ data: params.slug }).catch(() => null),
      getJobBySlug({ data: params.slug }).catch(() => null),
    ]);

    if (tag) {
      const effectiveTags = [
        params.slug,
        ...extraTags.filter((s) => s !== params.slug),
      ];
      const [initialData, tagStats] = await Promise.all([
        getJobs({
          data: { limit: 20, tags: effectiveTags },
        }) as Promise<PaginatedJobs>,
        getTagStats({ data: params.slug }).catch(() => null),
      ]);
      return {
        type: "tag" as const,
        tag,
        initialJobs: initialData.jobs,
        initialTotal: initialData.total,
        tagStats: tagStats as TagStatsData | null,
        extraTagSlugs: extraTags.filter((s) => s !== params.slug),
      };
    }

    if (job) {
      if (job.archiveDisposition === "retired" && import.meta.env.SSR) {
        const { setResponseStatus } =
          await import("@tanstack/react-start/server");
        setResponseStatus(410, "Gone");
      }
      // Find position-group tag for similar jobs
      const positionGroupTag = job.jobTags?.find(
        (t: { category?: { slug: string } | null }) =>
          t.category?.slug === "position-group",
      );

      const [subscriberData, similarData] = await Promise.all([
        getSubscriberCount().catch(() => ({ count: 0, display: "0" })),
        positionGroupTag
          ? getJobs({
              data: { tags: [positionGroupTag.slug], limit: 7 },
            }).catch(() => ({ jobs: [] }))
          : Promise.resolve({ jobs: [] }),
      ]);

      const similarJobs = ((similarData?.jobs ?? []) as JobWithRelations[])
        .filter((j: JobWithRelations) => j.slug !== job.slug)
        .slice(0, 6);

      return {
        type: "job" as const,
        job,
        subscriberDisplay: subscriberData.display,
        similarJobs,
        positionGroupTag: positionGroupTag
          ? {
              name: positionGroupTag.name,
              slug: positionGroupTag.slug,
              bgColor: positionGroupTag.category?.bgColor ?? null,
            }
          : null,
      };
    }

    throw notFound();
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};

    if (loaderData.type === "job") {
      const { job } = loaderData;
      const { title, description } = getJobMeta(job);
      const url = `${SITE_URL}/${job.slug}`;
      return {
        meta: [
          { title },
          { name: "description", content: description },
          { property: "og:title", content: title },
          { property: "og:description", content: description },
          { property: "og:url", content: url },
          { property: "og:type", content: "article" },
          { name: "twitter:card", content: "summary_large_image" },
          ...(shouldNoIndex(job)
            ? [{ name: "robots", content: "noindex, follow" }]
            : []),
        ],
        links: [{ rel: "canonical", href: url }],
      };
    }

    if (loaderData.type === "tag") {
      const title = getTagMetaTitle(loaderData.tag);
      const description = getTagMetaDescription(
        loaderData.tag,
        loaderData.initialTotal,
      );
      const url = `${SITE_URL}/${loaderData.tag.slug}`;
      const isThin = loaderData.initialTotal < MIN_INDEXABLE_JOBS;
      return {
        meta: [
          { title },
          { name: "description", content: description },
          { property: "og:title", content: title },
          { property: "og:description", content: description },
          { property: "og:url", content: url },
          { name: "twitter:card", content: "summary_large_image" },
          ...(isThin ? [{ name: "robots", content: "noindex, follow" }] : []),
        ],
        links: [{ rel: "canonical", href: url }],
      };
    }

    if (loaderData.type === "location") {
      const title = `Data Jobs in ${loaderData.displayName} | Data First Jobs`;
      const description = `Browse ${loaderData.initialTotal}+ data jobs in ${loaderData.displayName}. Find data engineering, analytics, and data science roles on Data First Jobs.`;
      const url = `${SITE_URL}/${loaderData.slug}`;
      const isThin = loaderData.initialTotal < MIN_INDEXABLE_JOBS;
      return {
        meta: [
          { title },
          { name: "description", content: description },
          { property: "og:title", content: title },
          { property: "og:description", content: description },
          { property: "og:url", content: url },
          { name: "twitter:card", content: "summary_large_image" },
          ...(isThin ? [{ name: "robots", content: "noindex, follow" }] : []),
        ],
        links: [{ rel: "canonical", href: url }],
      };
    }

    return {};
  },
  component: DynamicPage,
  notFoundComponent: NotFoundPage,
});

function DynamicPage() {
  const data = Route.useLoaderData();
  const { jobCategoriesWithTags, locations } = rootRoute.useRouteContext();

  useEffect(() => {
    if (data.type === "job") {
      trackPageView("job_detail", data.job.slug);
    } else if (data.type === "tag") {
      trackPageView("search");
    } else if (data.type === "location") {
      trackPageView("location", data.slug);
    }
  }, [data]);

  if (data.type === "job") {
    const breadcrumbItems = getJobBreadcrumbs(data.job);

    return (
      <section className="min-h-dvh">
        <JobPostingJsonLd job={data.job} />
        <BreadcrumbJsonLd
          items={breadcrumbItems.map((item) => ({
            name: item.label,
            url: "href" in item ? `${SITE_URL}${item.href}` : undefined,
          }))}
        />

        {/* Job Detail */}
        <JobDetailContent job={data.job} breadcrumbs={breadcrumbItems} />

        {/* Similar Jobs */}
        {data.similarJobs.length > 0 && data.positionGroupTag && (
          <div className="mx-auto my-20 max-w-4xl px-4 sm:px-8">
            <div className="mb-8 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl">
                Similar {toFieldName(data.positionGroupTag.name)} Jobs
              </h2>
              <Link
                to="/$slug"
                params={{ slug: data.positionGroupTag.slug }}
                className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900"
              >
                View all {toFieldName(data.positionGroupTag.name)} jobs
                <span className="text-gray-400">→</span>
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {data.similarJobs.map((job) => (
                <JobCard key={job.slug} job={job} variant="compact" />
              ))}
            </div>
            <div className="mt-8 text-center">
              <Link
                to="/"
                className="text-sm text-gray-500 transition-colors hover:text-gray-700"
              >
                Looking for something else? Search all data jobs →
              </Link>
            </div>
          </div>
        )}

        {/* Newsletter Inline CTA — full width, flush against footer */}
        <div className="-mb-20">
          <EmailCaptureForm
            variant="inline"
            cta="Like this role? Get carefully selected jobs like it, twice a week, straight to your inbox."
          />
        </div>
      </section>
    );
  }

  if (data.type === "location") {
    return (
      <>
        <BreadcrumbJsonLd
          items={[
            { name: "Home", url: SITE_URL },
            { name: `Data Jobs in ${data.displayName}` },
          ]}
        />
        {data.initialJobs.length > 0 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "ItemList",
                name: `Data Jobs in ${data.displayName}`,
                numberOfItems: data.initialTotal,
                itemListElement: data.initialJobs.map((job, i) => ({
                  "@type": "ListItem",
                  position: i + 1,
                  url: `${SITE_URL}/${job.slug}`,
                  name: job.role,
                })),
              }),
            }}
          />
        )}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: jsonLdSafe({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: [
                {
                  "@type": "Question",
                  name: `How many data jobs are available in ${data.displayName}?`,
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: `There are currently ${data.initialTotal} data jobs in ${data.displayName} listed on Data First Jobs. New positions are added daily.`,
                  },
                },
                {
                  "@type": "Question",
                  name: `What types of data jobs are available in ${data.displayName}?`,
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: `Data First Jobs lists data engineering, analytics, and data science positions in ${data.displayName}. Use the filters to narrow by skill, cloud stack, seniority, and more.`,
                  },
                },
              ],
            }),
          }}
        />
        <Hero
          title={
            <>
              Data jobs in{" "}
              <span className="text-polaris-orange">{data.displayName}</span>
            </>
          }
          subtitle={`Roles tagged by stack and seniority, so your next move in ${data.displayName} is easier to find.`}
          stats={{ jobs: data.initialTotal }}
          hideBrowseLink
        />
        <SearchClient
          key={data.slug}
          jobCategoriesWithTags={jobCategoriesWithTags}
          locations={locations}
          initialJobs={data.initialJobs}
          initialTotal={data.initialTotal}
          initialCountry={data.country}
          initialState={data.state}
          initialTagSlugs={data.extraTagSlugs}
        />
      </>
    );
  }

  // Tag page
  return (
    <TagListingView
      tag={data.tag}
      initialJobs={data.initialJobs}
      initialTotal={data.initialTotal}
      tagStats={data.tagStats}
      extraTagSlugs={data.extraTagSlugs}
      jobCategoriesWithTags={jobCategoriesWithTags}
      locations={locations}
    />
  );
}
